// src/pages/Settings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

/* ================================================================== */
/* Small building blocks                                               */
/* ================================================================== */

const Toggle = ({ checked, onChange, disabled = false, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
      checked ? 'bg-orange' : 'bg-gray-300 dark:bg-gray-600'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const PasswordField = ({
  id,
  label,
  value,
  onChange,
  autoComplete,
  minLength,
  required = true,
}) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          minLength={minLength}
          className="w-full p-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange transition"
        >
          <i
            className={`fas ${show ? 'fa-eye-slash' : 'fa-eye'} text-sm`}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
};

const PasswordStrength = ({ password }) => {
  const score = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 8) s++;
    if (password.length >= 12) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return Math.min(s, 4);
  })();

  if (!password) return null;

  const { label, color, width } = [
    { label: 'Too short', color: 'bg-red-500', width: '20%' },
    { label: 'Weak', color: 'bg-red-500', width: '30%' },
    { label: 'Fair', color: 'bg-yellow-500', width: '55%' },
    { label: 'Good', color: 'bg-lime-500', width: '80%' },
    { label: 'Strong', color: 'bg-green-500', width: '100%' },
  ][score];

  return (
    <div className="mt-1.5">
      <div className="h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-300`}
          style={{ width }}
        />
      </div>
      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
        {label}
      </p>
    </div>
  );
};

/* ================================================================== */
/* Settings                                                            */
/* ================================================================== */

const Settings = () => {
  const { user, isAdmin, isBoard, isMember } = useAuth();

  /* Appearance */
  const [darkMode, setDarkMode] = useState(
    () => typeof document !== 'undefined'
      && document.documentElement.classList.contains('dark'),
  );

  /* Notifications */
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [savingPrefs, setSavingPrefs] = useState(false);

  /* Password */
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  /* Feedback */
  const [message, setMessage] = useState({ type: '', text: '' });
  const messageTimerRef = useRef(null);

  const flashMessage = (type, text, ms = 3500) => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setMessage({ type, text });
    messageTimerRef.current = setTimeout(() => setMessage({ type: '', text: '' }), ms);
  };

  /* -------------------- Cleanup -------------------- */
  useEffect(() => {
    return () => {
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    };
  }, []);

  /* -------------------- Load preferences -------------------- */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        if (snap.exists()) {
          const prefs = snap.data().preferences || {};
          setEmailNotifications(prefs.emailNotifications !== false);
          setMessageNotifications(prefs.notifications !== false);
        }
      } catch (err) {
        console.error('Error loading preferences:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  /* -------------------- Notification permission -------------------- */
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(window.Notification.permission);
    }
  }, []);

  /* -------------------- Sync dark mode across tabs -------------------- */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'theme') {
        const isDark = e.newValue === 'dark';
        setDarkMode(isDark);
        document.documentElement.classList.toggle('dark', isDark);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /* -------------------- Dark mode -------------------- */
  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  /* -------------------- Preferences -------------------- */
  const savePreferences = async (overrides = {}) => {
    if (!user) return;
    setSavingPrefs(true);
    try {
      const payload = {
        emailNotifications:
          overrides.emailNotifications ?? emailNotifications,
        notifications:
          overrides.messageNotifications ?? messageNotifications,
      };
      await updateDoc(doc(db, 'users', user.uid), {
        preferences: payload,
      });
      flashMessage('success', 'Preferences saved');
    } catch (err) {
      console.error(err);
      flashMessage('error', 'Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  /* Auto-save individual toggles */
  const handleEmailNotificationsToggle = (next) => {
    setEmailNotifications(next);
    savePreferences({ emailNotifications: next });
  };

  const handleMessageNotificationsToggle = (next) => {
    setMessageNotifications(next);
    savePreferences({ messageNotifications: next });
  };

  const requestNotificationPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      flashMessage('error', 'Browser notifications are not supported here.');
      return;
    }
    try {
      const perm = await window.Notification.requestPermission();
      setNotificationPermission(perm);
      if (perm === 'granted') {
        setMessageNotifications(true);
        savePreferences({ messageNotifications: true });
      } else if (perm === 'denied') {
        flashMessage(
          'error',
          'Notifications blocked. Enable them in your browser settings.',
        );
      }
    } catch (err) {
      console.error(err);
      flashMessage('error', 'Failed to request notification permission');
    }
  };

  /* -------------------- Password change -------------------- */
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      flashMessage('error', 'New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      flashMessage('error', 'Password must be at least 6 characters.');
      return;
    }
    if (newPassword === currentPassword) {
      flashMessage('error', 'New password must be different from the current one.');
      return;
    }

    setChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
      flashMessage('success', 'Password updated successfully!');
      setTimeout(() => setPasswordSaved(false), 2000);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        flashMessage('error', 'Current password is incorrect.');
      } else if (err.code === 'auth/weak-password') {
        flashMessage('error', 'Password is too weak. Use at least 6 characters.');
      } else if (err.code === 'auth/too-many-requests') {
        flashMessage('error', 'Too many attempts. Please try again later.');
      } else {
        flashMessage('error', 'Failed to update password.');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  /* -------------------- Sign out -------------------- */
  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  };

  /* -------------------- Not logged in -------------------- */
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
            Please log in
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You need to be logged in to access settings.
          </p>
        </div>
      </div>
    );
  }

  /* -------------------- Render -------------------- */
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-28 pb-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white">
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Manage your account preferences and security
          </p>
        </div>

        {/* Feedback toast */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              role="status"
              className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${
                message.type === 'success'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              }`}
            >
              <i
                className={`fas ${
                  message.type === 'success'
                    ? 'fa-check-circle'
                    : 'fa-exclamation-circle'
                } mt-0.5`}
                aria-hidden="true"
              />
              <span>{message.text}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-6">
          {/* ============ Appearance ============ */}
          <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Appearance
            </h2>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-800 dark:text-white">
                  Dark Mode
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Switch between light and dark theme
                </p>
              </div>
              <Toggle
                checked={darkMode}
                onChange={toggleDarkMode}
                ariaLabel="Toggle dark mode"
              />
            </div>
          </section>

          {/* ============ Notifications ============ */}
          <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Notifications
            </h2>

            <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <p className="font-medium text-gray-800 dark:text-white">
                  Email Notifications
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Receive email updates about events and news
                </p>
              </div>
              <Toggle
                checked={emailNotifications}
                onChange={handleEmailNotificationsToggle}
                disabled={savingPrefs}
                ariaLabel="Toggle email notifications"
              />
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-800 dark:text-white">
                  Browser Notifications
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Get browser notifications for new messages
                </p>
                {notificationPermission === 'denied' && (
                  <p className="text-xs text-red-500 mt-1">
                    <i className="fas fa-info-circle mr-1" aria-hidden="true" />
                    Blocked in browser settings
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Toggle
                  checked={messageNotifications}
                  onChange={handleMessageNotificationsToggle}
                  disabled={savingPrefs}
                  ariaLabel="Toggle browser notifications"
                />
                {notificationPermission !== 'granted' && (
                  <button
                    type="button"
                    onClick={requestNotificationPermission}
                    className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2.5 py-1.5 rounded transition whitespace-nowrap"
                  >
                    Enable
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ============ Security ============ */}
          <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Security
            </h2>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <PasswordField
                id="current-password"
                label="Current Password"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
              />

              <div>
                <PasswordField
                  id="new-password"
                  label="New Password"
                  value={newPassword}
                  onChange={setNewPassword}
                  autoComplete="new-password"
                  minLength={6}
                />
                <PasswordStrength password={newPassword} />
              </div>

              <PasswordField
                id="confirm-password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                autoComplete="new-password"
              />

              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">
                  <i className="fas fa-exclamation-circle mr-1" aria-hidden="true" />
                  Passwords do not match
                </p>
              )}

              <button
                type="submit"
                disabled={
                  changingPassword ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
                className="bg-orange text-white px-4 py-2 rounded-lg hover:bg-orange-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {changingPassword ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    Updating…
                  </>
                ) : passwordSaved ? (
                  <>
                    <i className="fas fa-check" aria-hidden="true" />
                    Saved
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            </form>
          </section>

          {/* ============ Account Info ============ */}
          <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Account Information
            </h2>
            <div className="space-y-2 text-sm">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Email:</span> {user.email}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Role:</span>{' '}
                {isAdmin
                  ? 'Administrator'
                  : isBoard
                  ? 'Board Member'
                  : isMember
                  ? 'Member'
                  : 'Pending Approval'}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Member since:</span>{' '}
                {user.metadata?.creationTime
                  ? new Date(user.metadata.creationTime).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleSignOut}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-2"
              >
                <i className="fas fa-sign-out-alt" aria-hidden="true" />
                Sign out
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Settings;